import type { shell } from 'punica';

import { Helpers } from '../view/helpers.js';
import tabScrollbarCss from './contentTabs.css';

export interface ContentTabOpenOptions {
  path: string;
  title?: string;
  viewType?: string;
}

interface TabEntry {
  id: string;
  path: string;
  title: string;
  /**
   * Whether `title` is just the file name, and so should follow the path
   * through a rename. False when the opener supplied its own label (a Compose
   * designer, a database session) — that names the view, not the file, and a
   * rename must not overwrite it.
   */
  titleFollowsPath: boolean;
  viewType?: string;
  tabElement: HTMLElement;
  labelElement: HTMLElement;
  /**
   * The unsaved-changes dot, a SIBLING of the label rather than a suffix
   * inside it. The label is capped at 150px and ellipsized, and a file name
   * long enough to be truncated is exactly the case where a trailing marker
   * fell past the cut — appending " •" to the text left it invisible on every
   * tab that needed it most.
   */
  dirtyElement: HTMLElement;
  container: HTMLElement;
  panel: HTMLElement;
  dirty: boolean;
}

/** Last path segment — the file name a tab is labelled with. */
function basename(path: string): string {
  return path.split('/').pop() || path;
}

/** `src/app.ts` under a `src` → `lib` rename becomes `lib/app.ts`. */
function reparent(path: string, oldRoot: string, newRoot: string): string {
  if (path === oldRoot) return newRoot;
  return `${newRoot}${path.slice(oldRoot.length)}`;
}

class ContentTabsManager implements shell.ContentTabsApi {
  #initialized = false;
  #tabList: HTMLElement | null = null;
  #panelRoot: HTMLElement | null = null;
  #entriesByPath: Map<string, TabEntry> = new Map();
  #contextTargetId: string | null = null;

  #ensureRoot(): void {
    if (this.#initialized) return;

    const contentSlot = punica.shell.Layout.manager.getSlot('content');
    const host = contentSlot?.content;
    if (!host) {
      // eslint-disable-next-line no-console
      console.warn('[ContentTabs] content host not found');
      return;
    }

    host.innerHTML = '';

    const root = Helpers.createBox({
      attrs: { id: 'content-tabs-root' },
      style: { height: '100%', display: 'flex', flexDirection: 'column' }
    });

    const tabBarBox = Helpers.createBox({
      attrs: { id: 'content-tabs-bar' },
      style: {
        height: '40px',
        flex: '0 0 auto',
        display: 'flex',
        alignItems: 'center',
        // Scroll the tab strip horizontally once the tabs no longer fit,
        // rather than clipping the overflow. The cross axis stays locked to
        // the 40px bar.
        overflowX: 'auto',
        overflowY: 'hidden',
        backgroundColor: 'var(--background-paper)'
      }
    });

    // A vertical wheel over the bar scrolls it horizontally (VS Code behaviour),
    // so a plain mouse wheel can still reach off-screen tabs.
    tabBarBox.addEventListener(
      'wheel',
      (e: WheelEvent) => {
        if (tabBarBox.scrollWidth <= tabBarBox.clientWidth) return;
        if (e.deltaY === 0) return;
        tabBarBox.scrollLeft += e.deltaY;
        e.preventDefault();
      },
      { passive: false }
    );

    const tabList = Helpers.createTab({
      orientation: 'horizontal',
      style: {
        // Grow to the tabs' combined width so the bar can scroll to reach
        // them; min-width keeps the strip filling the bar when only a few are
        // open.
        width: 'max-content',
        minWidth: '100%',
        flex: '0 0 auto',
        height: '100%'
      },
      onChange: (value) => {
        if (!value) return;
        this.#setSelected(value);
      }
    });

    tabBarBox.appendChild(tabList);

    // Thin horizontal scrollbar for the tab strip (the host owns the overflow).
    const scrollStyle = document.createElement('style');
    scrollStyle.textContent = tabScrollbarCss;
    tabBarBox.appendChild(scrollStyle);

    const panelRoot = Helpers.createBox({
      attrs: { id: 'content-tabs-panels' },
      style: { flex: '1', height: 'calc(100% - 40px)' }
    });

    root.appendChild(tabBarBox);
    root.appendChild(panelRoot);

    // Context menu for tabs. The three actions never change — only the tab
    // they apply to does, and that is tracked separately in #contextTargetId.
    const ctxMenu = Helpers.createContextMenu({
      attrs: { id: 'content-tabs-menu' },
      target: '#content-tabs-root',
      items: [
        { label: 'Close', value: 'close' },
        { label: 'Close Others', value: 'closeOthers' },
        { label: 'Close All', value: 'closeAll' }
      ],
      onSelect: (value) => {
        const activeId =
          this.#contextTargetId ||
          (this.#tabList ? this.#tabList.getAttribute('value') || '' : '');

        switch (value) {
          case 'close':
            if (activeId) {
              this.closeById(activeId);
            }
            break;
          case 'closeOthers':
            if (activeId) {
              this.closeOthersById(activeId);
            }
            break;
          case 'closeAll':
            this.closeAll();
            break;
          default:
            break;
        }

        this.#contextTargetId = null;
      }
    });

    root.appendChild(ctxMenu);

    host.appendChild(root);

    this.#tabList = tabList;
    this.#panelRoot = panelRoot;
    this.#initialized = true;
  }

  #setSelected(id: string): void {
    if (!this.#tabList || !this.#panelRoot) return;

    this.#tabList.setAttribute('value', id);

    // Highlight selected tab item with background color.
    const tabItems =
      this.#tabList.querySelectorAll<HTMLElement>('punica-tab-item');
    tabItems.forEach((item) => {
      const value = item.getAttribute('value');
      if (value === id) {
        item.style.backgroundColor = 'var(--background-main)';
        // Reveal the active tab if the strip has scrolled it out of view.
        item.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      } else {
        item.style.backgroundColor = '';
      }
    });

    const panels =
      this.#panelRoot.querySelectorAll<HTMLElement>('punica-tab-panel');

    panels.forEach((panel) => {
      panel.setAttribute('selectedvalue', id);
    });

    // Inform file explorer (if available) so it can reveal the active file.
    try {
      const anyWindow = window as any;
      const commands = anyWindow?.punica?.kernel?.Commands?.manager;
      if (commands && typeof commands.executeCommand === 'function') {
        // In the current implementation, tab id === file path.
        commands.executeCommand('file-explorer.revealFile', id);
      }
    } catch (err) {
      // Best-effort only; ignore if explorer extension is not present.
      // eslint-disable-next-line no-console
      console.warn('[ContentTabs] Failed to notify file-explorer', err);
    }

    // Keep the ActivityBar highlight in sync with the active content tab so a
    // command-only tool (Settings / Capability Catalog) shows as active while
    // its tab is foreground, and clears when another tab (e.g. a code file)
    // becomes active.
    this.#syncActivityBar(id);
  }

  /** Notify the ActivityBar of the active tab's content viewType. */
  #syncActivityBar(id: string | null): void {
    try {
      const entry = id
        ? Array.from(this.#entriesByPath.values()).find((e) => e.id === id)
        : undefined;
      punica.shell.ActivityBar.manager.syncContentTool?.(
        entry?.viewType ?? null
      );
    } catch {
      // Best-effort; the ActivityBar may be unavailable in headless/tests.
    }
  }

  open(options: ContentTabOpenOptions): HTMLElement | null {
    this.#ensureRoot();
    if (!this.#tabList || !this.#panelRoot) return null;

    const existing = this.#entriesByPath.get(options.path);
    if (existing) {
      this.#setSelected(existing.id);
      return existing.container;
    }

    const id = options.path;
    const derivedTitle = basename(options.path);
    const title = options.title ?? derivedTitle;
    const titleFollowsPath = title === derivedTitle;

    const label = Helpers.createTypography(title, {
      attrs: { truncate: '1', title: options.path },
      style: { maxWidth: '150px' }
    });

    const closeBtn = Helpers.createIconButton(
      'fa-duotone fa-solid fa-xmark',
      (ev) => {
        ev.stopPropagation();
        this.close(options.path);
      },
      { size: 'small', title: 'Close' }
    );

    // `flex: 0 0 auto` so the dot keeps its width while the label absorbs the
    // truncation, and a `title` because a 6px dot has to be able to say what
    // it means.
    const dirtyElement = Helpers.createElement('span', {
      attrs: {
        role: 'img',
        'aria-label': 'Unsaved changes',
        title: 'Unsaved changes'
      },
      style: {
        display: 'none',
        flex: '0 0 auto',
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        backgroundColor: 'var(--text-primary)'
      }
    });

    const row = Helpers.createRow({
      alignItems: 'center',
      wrap: 'nowrap',
      spacing: '0.5',
      style: { maxWidth: '200px' },
      children: [
        Helpers.createCol({ children: [label] }),
        Helpers.createCol({ children: [dirtyElement] }),
        Helpers.createCol({ children: [closeBtn] })
      ]
    });

    const tabItem = Helpers.createTabItem({
      value: id,
      className: 'content-file-type',
      style: {
        maxWidth: '200px',
        flex: '0 0 auto',
        padding:
          'var(--spacing-8) var(--spacing-4) var(--spacing-8) var(--spacing-12)'
      },
      children: [row]
    });

    tabItem.addEventListener('contextmenu', () => {
      this.#contextTargetId = id;
      this.#setSelected(id);
    });

    this.#tabList.appendChild(tabItem);

    const container = Helpers.createElement('div', {
      style: { height: '100%', width: '100%', boxSizing: 'border-box' }
    });

    const panel = Helpers.createTabPanel({
      value: id,
      selectedValue: id,
      style: { height: '100%', width: '100%', boxSizing: 'border-box' },
      children: [container]
    });
    this.#panelRoot.appendChild(panel);

    const entry: TabEntry = {
      id,
      path: options.path,
      title,
      titleFollowsPath,
      viewType: options.viewType,
      tabElement: tabItem,
      labelElement: label,
      dirtyElement,
      container,
      panel,
      dirty: false
    };

    this.#entriesByPath.set(options.path, entry);
    this.#setSelected(id);

    return container;
  }
  async close(path: string): Promise<void> {
    const entry = this.#entriesByPath.get(path);
    if (!entry) return;
    if (!(await this.#confirmClose(entry))) return;
    this.#removeTab(entry);
  }

  async closeById(id: string): Promise<void> {
    const entry = Array.from(this.#entriesByPath.values()).find(
      (e) => e.id === id
    );
    if (!entry) return;
    if (!(await this.#confirmClose(entry))) return;
    this.#removeTab(entry);
  }

  async closeOthers(path: string): Promise<void> {
    const entry = this.#entriesByPath.get(path);
    if (!entry) return;
    await this.closeOthersById(entry.id);
  }

  async closeOthersById(id: string): Promise<void> {
    const others = Array.from(this.#entriesByPath.values()).filter(
      (e) => e.id !== id
    );
    for (const e of others) {
      // A cancel on any tab aborts the whole operation (matches VS Code).
      if (!(await this.#confirmClose(e))) return;
      this.#removeTab(e);
    }
    this.#setSelected(id);
  }

  async closeAll(): Promise<void> {
    const entries = Array.from(this.#entriesByPath.values());
    for (const e of entries) {
      if (!(await this.#confirmClose(e))) return;
      this.#removeTab(e);
    }
  }

  /** Unconditional teardown: remove the tab's DOM and select the next tab. */
  #removeTab(entry: TabEntry): void {
    if (!this.#tabList || !this.#panelRoot) return;

    if (entry.tabElement.parentElement === this.#tabList) {
      this.#tabList.removeChild(entry.tabElement);
    }
    if (entry.panel.parentElement === this.#panelRoot) {
      this.#panelRoot.removeChild(entry.panel);
    }

    this.#entriesByPath.delete(entry.path);
    // Whoever rendered into this tab is keying its own state by the same path
    // (Monaco instances, notebook controllers) and has no other way to learn
    // the tab is gone — without this the instance is never disposed.
    this.#publish('shell.contentTabs.didClose', { path: entry.path });

    const remaining = Array.from(this.#entriesByPath.values());
    if (remaining.length > 0) {
      const next = remaining[remaining.length - 1];
      this.#setSelected(next.id);
    } else {
      this.#tabList.removeAttribute('value');
      // No tabs left — clear any content-tool highlight in the ActivityBar.
      this.#syncActivityBar(null);
    }
  }

  /**
   * Guards a close against unsaved edits. Returns true when it is safe to
   * remove the tab (clean, saved, or the user chose to discard), false to
   * abort (cancel, ESC, or a failed save).
   */
  async #confirmClose(entry: TabEntry): Promise<boolean> {
    if (!entry.dirty) return true;

    // Make the closing tab active so the save command targets it.
    this.#setSelected(entry.id);

    const baseName = entry.title || entry.path.split('/').pop() || entry.path;
    const action = await punica.shell.View.showDialog({
      title: 'Unsaved changes',
      message: `"${baseName}" has unsaved changes. Do you want to save before closing?`,
      tone: 'warning',
      buttons: [
        { label: 'Cancel', variant: 'text', action: 'cancel' },
        { label: "Don't Save", variant: 'outlined', action: 'discard' },
        { label: 'Save', variant: 'filled', color: 'primary', action: 'save' }
      ]
    });

    if (action === 'save') {
      try {
        const commands = punica?.kernel?.Commands?.manager;
        if (commands && typeof commands.executeCommand === 'function') {
          await commands.executeCommand('editor.saveActiveFile');
        }
        return true;
      } catch (err) {
        await punica.shell.View.showAlert({
          title: 'Save failed',
          message: `Could not save "${baseName}". The tab was left open.`,
          variant: 'error'
        });
        // eslint-disable-next-line no-console
        console.warn('[ContentTabs] save before close failed', err);
        return false;
      }
    }

    return action === 'discard';
  }

  setDirty(path: string, dirty: boolean): void {
    const entry = this.#entriesByPath.get(path);
    if (!entry) return;
    entry.dirty = dirty;
    this.#renderLabel(entry);
    // Mirror into the substrate's working-copy registry so a caller can ask
    // "would deleting this lose unsaved work?" without knowing that content
    // tabs exist. The file explorer's delete confirmation reads it.
    try {
      punica.runtime.fileOperations?.setDirty(path, dirty);
    } catch {
      // Older host without the surface; the tab marker above still works.
    }
  }

  isDirty(path: string): boolean {
    return this.#entriesByPath.get(path)?.dirty ?? false;
  }

  list(): readonly shell.ContentTabInfo[] {
    return Array.from(this.#entriesByPath.values()).map((entry) => ({
      path: entry.path,
      title: entry.title,
      viewType: entry.viewType,
      dirty: entry.dirty
    }));
  }

  /**
   * Re-key the tab at `oldPath` to `newPath`, keeping its content, dirty
   * marker and selection.
   *
   * A tab's id *is* its path, so a rename is not a cosmetic title update —
   * every lookup the tab takes part in (`getActivePath`, the panel's
   * `selectedvalue`, the explorer's reveal) goes through that id, and leaving
   * it on the old path is what makes a save after a rename write to the wrong
   * file or to nothing at all.
   *
   * Directories carry their contents: renaming `src` has to move `src/app.ts`
   * with it, so the match is prefix-based.
   */
  rename(oldPath: string, newPath: string): void {
    if (!oldPath || !newPath || oldPath === newPath) return;

    const affected = this.#entriesFor(oldPath);
    if (affected.length === 0) return;

    const activeId = this.#tabList?.getAttribute('value') ?? null;
    let newActiveId: string | null = null;

    for (const entry of affected) {
      const nextPath = reparent(entry.path, oldPath, newPath);
      const wasActive = entry.id === activeId;

      this.#entriesByPath.delete(entry.path);
      entry.path = nextPath;
      entry.id = nextPath;
      if (entry.titleFollowsPath) entry.title = basename(nextPath);
      entry.tabElement.setAttribute('value', nextPath);
      entry.panel.setAttribute('value', nextPath);
      entry.labelElement.setAttribute('title', nextPath);
      this.#renderLabel(entry);
      this.#entriesByPath.set(nextPath, entry);

      if (wasActive) newActiveId = nextPath;
    }

    // Panels select by id, so the strip has to be re-pointed at the new id or
    // the foreground tab renders empty.
    if (newActiveId) this.#setSelected(newActiveId);

    this.#publish('shell.contentTabs.didRename', { oldPath, newPath });
  }

  /**
   * Close the tab at `path` (and everything beneath it, for a directory)
   * without prompting.
   *
   * `close()` asks about unsaved changes, which is right while the file still
   * exists. It is wrong here: the caller is telling us the file is already
   * gone, so "Save before closing?" would offer a choice that cannot be
   * honoured.
   */
  forceClose(path: string): void {
    for (const entry of this.#entriesFor(path)) {
      this.#removeTab(entry);
    }
  }

  /**
   * The tab at `path` plus, when `path` is a directory, every tab beneath it.
   * Snapshotted into an array because callers mutate `#entriesByPath` while
   * iterating.
   */
  #entriesFor(path: string): TabEntry[] {
    return Array.from(this.#entriesByPath.values()).filter(
      (entry) => entry.path === path || entry.path.startsWith(`${path}/`)
    );
  }

  /**
   * Paint the tab's label and its unsaved-changes dot.
   *
   * Two elements on purpose: the label truncates, the dot must not. Anything
   * appended to the label's own text is the first thing the ellipsis eats.
   */
  #renderLabel(entry: TabEntry): void {
    entry.labelElement.textContent = entry.title || basename(entry.path);
    entry.dirtyElement.style.display = entry.dirty ? 'inline-block' : 'none';
  }

  /** Best-effort tab-lifecycle notification; observability never blocks a tab. */
  #publish(name: string, payload: Record<string, unknown>): void {
    try {
      punica.kernel.Events.manager.publish(name, payload);
    } catch {
      // Event bus unavailable (headless / tests) — nothing here is
      // load-bearing for the tab itself.
    }
  }

  getActivePath(): string | null {
    if (!this.#tabList) return null;
    const id = this.#tabList.getAttribute('value');
    if (!id) return null;
    const entry = Array.from(this.#entriesByPath.values()).find(
      (e) => e.id === id
    );
    return entry?.path ?? null;
  }

  getActiveViewType(): string | null {
    if (!this.#tabList) return null;
    const id = this.#tabList.getAttribute('value');
    if (!id) return null;
    const entry = Array.from(this.#entriesByPath.values()).find(
      (e) => e.id === id
    );
    return entry?.viewType ?? null;
  }
}

const contentTabs: shell.ContentTabsApi = new ContentTabsManager();

export default contentTabs;
